/**
 * Practice timer — the pure state machine behind the plugin's
 * startTimer / updateTimer / stopTimer / _armStop / _disarmStop /
 * maybeAutoStart. No DOM, no intervals: the UI calls `tick(now)` once a
 * second and renders whatever comes back.
 *
 * Rules that MUST survive (see stop-button-guard.md, timer-autostart-and-audio.md):
 *  - Stop is two taps. First tap arms (STOP_ARM_MS window), second commits.
 *    Every stop path disarms first.
 *  - Today's auto-start is consumed once a timer is running inside the
 *    practice window (manual or automatic); a test run earlier in the day
 *    does NOT consume it. See practiceWindowReached.
 *  - Auto-start catches UP to the real start time rather than starting at 0.
 *  - Auto-start only fires for the head coach, only for the pinned plan,
 *    only between the first period's time and the plan's end, once per day.
 *  - Audio cues are host-only. Followers get haptic / notification only.
 */
import type { PlanData } from '../types';
import { timeToMin, planEndMin } from './math';

export const STOP_ARM_MS = 4000;
export const AUTOSTART_FALLBACK_MIN = 120;
export const SESSION_MAX_AGE_MS = 8 * 60 * 60 * 1000;

export interface TimerState {
  running: boolean;
  /** epoch ms of the moment period 0 "started" (back-dated when starting mid-plan). */
  anchorMs: number | null;
  /** True when this device published the shared session (host). */
  isHost: boolean;
  /** Host only: did the server accept the session? False = LOCAL ONLY. */
  published: boolean;
  stopArmed: boolean;
  stopArmedAt: number | null;
  lastAlertedIdx: number;
  warnedIdx: number;
  /** period index that already got its 30-second buzz */
  thirtyIdx: number;
}

export function initialTimerState(): TimerState {
  return {
    running: false,
    anchorMs: null,
    isHost: false,
    published: false,
    stopArmed: false,
    stopArmedAt: null,
    lastAlertedIdx: -1,
    warnedIdx: -1,
    thirtyIdx: -1,
  };
}

/** warning = 1 min left (banner turns red); thirty = 30 s left (short buzz); periodChange = long buzz. */
export type TimerCue = 'periodChange' | 'warning' | 'thirty' | 'done';

export interface TimerView {
  running: boolean;
  /** -1 before the first period. */
  periodIdx: number;
  periodName: string;
  /** "12:34", "+3:05" in the last period, "—" before the first. */
  text: string;
  /** Seconds remaining in the current period; null in/after last period. */
  remainingSec: number | null;
  isLastPeriod: boolean;
  warning: boolean;
  /** 0..100 */
  progressPct: number;
  status: string;
  cues: TimerCue[];
}

/** Elapsed ms from period-0 start to `now`; the anchor is period 0's wall-clock start. */
function elapsedMin(anchorMs: number, nowMs: number): number {
  return (nowMs - anchorMs) / 60000;
}

/** Plugin's periodEndTime(): minute-of-day the period ends, or null for the last. */
export function periodEndMin(plan: PlanData, idx: number): number | null {
  if (idx < 0 || idx >= plan.periods.length) return null;
  if (idx < plan.periods.length - 1) return timeToMin(plan.periods[idx + 1]!.time);
  return null;
}

/** Which period is current at `nowMin` (minute-of-day, fractional). -1 before the first. */
export function currentPeriodIdx(plan: PlanData, nowMin: number): number {
  for (let i = plan.periods.length - 1; i >= 0; i--) {
    if (timeToMin(plan.periods[i]!.time) <= nowMin) return i;
  }
  return -1;
}

function fmtMS(totalMin: number, prefix = ''): string {
  const mins = Math.floor(totalMin);
  const secs = Math.floor((totalMin - mins) * 60);
  return `${prefix}${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * Compute the timer view for `now`. Also returns the next state so the
 * caller can persist lastAlertedIdx / warnedIdx and fire cues once.
 */
export function tick(
  plan: PlanData,
  state: TimerState,
  nowMs: number,
  periodName: (idx: number) => string,
): { view: TimerView; state: TimerState } {
  if (!state.running || state.anchorMs === null || !plan.periods.length) {
    return {
      view: {
        running: false,
        periodIdx: -1,
        periodName: 'Practice',
        text: '—',
        remainingSec: null,
        isLastPeriod: false,
        warning: false,
        progressPct: 0,
        status: plan.periods.length ? 'Tap a time to choose start · ▶ Start' : '⏱ Timer ready',
        cues: [],
      },
      state,
    };
  }
  const firstMin = timeToMin(plan.periods[0]!.time);
  const nowMin = firstMin + elapsedMin(state.anchorMs, nowMs);
  const idx = currentPeriodIdx(plan, nowMin);
  const cues: TimerCue[] = [];
  let { lastAlertedIdx, warnedIdx, thirtyIdx } = state;

  if (idx !== lastAlertedIdx && idx >= 0) {
    // don't cue on period 0 at start
    if (lastAlertedIdx >= 0) cues.push('periodChange');
    lastAlertedIdx = idx;
  }

  const pname = idx >= 0 ? periodName(idx) : 'Practice';
  const isLastPeriod = idx === plan.periods.length - 1;
  const nextStart = idx >= 0 && !isLastPeriod ? timeToMin(plan.periods[idx + 1]!.time) : null;
  const remaining = nextStart !== null ? nextStart - nowMin : null;

  let view: TimerView;
  if (remaining !== null && idx >= 0) {
    const isWarning = remaining <= 1 && remaining > 0;
    const periodStart = timeToMin(plan.periods[idx]!.time);
    const duration = nextStart! - periodStart;
    const pct = duration > 0 ? Math.min(100, Math.max(0, ((nowMin - periodStart) / duration) * 100)) : 0;
    if (isWarning && warnedIdx !== idx) {
      warnedIdx = idx;
      cues.push('warning');
    }
    if (remaining <= 0.5 && remaining > 0 && thirtyIdx !== idx) {
      thirtyIdx = idx;
      cues.push('thirty');
    }
    view = {
      running: true,
      periodIdx: idx,
      periodName: pname,
      text: fmtMS(Math.max(0, remaining)),
      remainingSec: Math.max(0, Math.round(remaining * 60)),
      isLastPeriod: false,
      warning: isWarning,
      progressPct: pct,
      status: `${pname} · ${Math.floor(remaining)}m ${String(Math.floor((remaining % 1) * 60)).padStart(2, '0')}s left`,
      cues,
    };
  } else if (isLastPeriod && idx >= 0) {
    const elapsed = nowMin - timeToMin(plan.periods[idx]!.time);
    view = {
      running: true,
      periodIdx: idx,
      periodName: pname,
      text: fmtMS(Math.max(0, elapsed), '+'),
      remainingSec: null,
      isLastPeriod: true,
      warning: false,
      progressPct: 100,
      status: `${pname} — last period`,
      cues,
    };
  } else {
    view = {
      running: true,
      periodIdx: -1,
      periodName: 'Practice',
      text: '—',
      remainingSec: null,
      isLastPeriod: false,
      warning: false,
      progressPct: 0,
      status: 'Waiting for first period…',
      cues,
    };
  }
  return { view, state: { ...state, lastAlertedIdx, warnedIdx, thirtyIdx } };
}

/**
 * Offset (ms already elapsed) for a start. Either back-date to a wall-clock
 * anchor (auto-start catch-up, clamped ≥ 0 so a skewed clock can't back-date
 * into next week) or start from a chosen period index.
 */
export function startOffsetMs(
  plan: PlanData,
  opts: { startFromIdx?: number; anchorMs?: number; nowMs: number },
): number {
  const first = plan.periods[0];
  if (!first) return 0;
  if (opts.anchorMs !== undefined) return Math.max(0, opts.nowMs - opts.anchorMs);
  const firstMin = timeToMin(first.time);
  const from = plan.periods[opts.startFromIdx ?? 0] ?? first;
  return (timeToMin(from.time) - firstMin) * 60000;
}

export function startLocal(state: TimerState, anchorMs: number, isHost: boolean, published: boolean): TimerState {
  return {
    ...state,
    running: true,
    anchorMs,
    isHost,
    published,
    stopArmed: false,
    stopArmedAt: null,
    lastAlertedIdx: -1,
    warnedIdx: -1,
    thirtyIdx: -1,
  };
}

export function stopLocal(state: TimerState): TimerState {
  return {
    ...state,
    running: false,
    anchorMs: null,
    isHost: false,
    published: false,
    stopArmed: false,
    stopArmedAt: null,
    lastAlertedIdx: -1,
    warnedIdx: -1,
    thirtyIdx: -1,
  };
}

// ─── Stop guard ─────────────────────────────────────────────────────────────
export type StopTapResult = { action: 'start' } | { action: 'armed' } | { action: 'stop' };

/**
 * Plugin's toggleTimer(): not running → start; running & armed → stop;
 * running & idle → arm. The UI is responsible for calling `disarmIfExpired`
 * (or re-checking `stopArmExpired`) so a stale arm can't be tripped.
 */
export function onStopButtonTap(state: TimerState, nowMs: number): { state: TimerState; result: StopTapResult } {
  if (!state.running) return { state, result: { action: 'start' } };
  const armed = state.stopArmed && !stopArmExpired(state, nowMs);
  if (armed) return { state: disarmStop(state), result: { action: 'stop' } };
  return { state: { ...state, stopArmed: true, stopArmedAt: nowMs }, result: { action: 'armed' } };
}

export function stopArmExpired(state: TimerState, nowMs: number): boolean {
  return state.stopArmed && state.stopArmedAt !== null && nowMs - state.stopArmedAt >= STOP_ARM_MS;
}

export function disarmStop(state: TimerState): TimerState {
  if (!state.stopArmed) return state;
  return { ...state, stopArmed: false, stopArmedAt: null };
}

// ─── Auto-start ─────────────────────────────────────────────────────────────
export interface AutoStartInput {
  isHead: boolean;
  timer: TimerState;
  /** Shared session currently live on the server (already filtered for age). */
  sessionLive: boolean;
  /** Name/id of the pinned plan, or null. */
  postedPlanId: string | null;
  currentPlanId: string | null;
  autoStartDoneToday: boolean;
  plan: PlanData | null;
  nowMs: number;
  /** Midnight (local) of today in epoch ms; injected so tests are TZ-stable. */
  todayMidnightMs: number;
}

/**
 * Today's auto-start window for a plan: first period's time → last period's
 * time (or first + AUTOSTART_FALLBACK_MIN for a one-period plan). Null when
 * the plan has no periods.
 */
export function autoStartWindow(plan: PlanData | null, todayMidnightMs: number): { startMs: number; endMs: number } | null {
  if (!plan || !plan.periods.length) return null;
  const first = timeToMin(plan.periods[0]!.time);
  const last = timeToMin(plan.periods[plan.periods.length - 1]!.time);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  const endMin = last > first ? last : first + AUTOSTART_FALLBACK_MIN;
  return { startMs: todayMidnightMs + first * 60000, endMs: todayMidnightMs + endMin * 60000 };
}

/**
 * Whether today's auto-start is used up right now. It is used up only when a
 * timer is actually running INSIDE the practice window — not by a manual
 * start. So a coach can play with the timer at 9 a.m. (or 3:30 for a 4:00
 * practice, and stop it) and auto-start still fires at 4:00; but a practice
 * started early by hand is running at 4:00, so stopping it at 4:05 does not
 * let auto-start fire straight back.
 */
export function practiceWindowReached(timer: TimerState, plan: PlanData | null, nowMs: number, todayMidnightMs: number): boolean {
  if (!timer.running) return false;
  const w = autoStartWindow(plan, todayMidnightMs);
  return !!w && nowMs >= w.startMs && nowMs < w.endMs;
}

/**
 * Returns the wall-clock anchor to catch up to, or null when auto-start must
 * not fire. Mirrors the four guards in the plugin's maybeAutoStart().
 */
export function autoStartAnchor(i: AutoStartInput): number | null {
  if (!i.isHead) return null;
  if (i.timer.running || i.timer.isHost) return null;
  if (i.sessionLive) return null;
  if (!i.postedPlanId) return null;
  if (i.autoStartDoneToday) return null;
  if (i.currentPlanId !== i.postedPlanId) return null;
  const w = autoStartWindow(i.plan, i.todayMidnightMs);
  if (!w) return null;
  if (i.nowMs < w.startMs || i.nowMs >= w.endMs) return null;
  return w.startMs;
}

export function autoStartKey(d: Date): string {
  return `autostart_${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A session is only "live" if it's recent (SESSION_MAX_AGE_MS). */
export function sessionIsLive(startedAtMs: number | null | undefined, serverNowMs: number): boolean {
  if (startedAtMs === null || startedAtMs === undefined) return false;
  return serverNowMs - startedAtMs <= SESSION_MAX_AGE_MS;
}

export { planEndMin };
